<template>
  <div class="users-list">
    <section class="user-steps">
      <div class="container-fluid">
        <!-- Header -->
        <div class="row mb-4">
          <div class="col-12 d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <h5 class="mb-0 mr-3">Gestión de Usuarios (Sold-to)</h5>
              <router-link :to="route">
                <img class="d-none d-sm-block" src="@/assets/img/backspace.svg" alt="backspace" width="30">
              </router-link>
            </div>
            <router-link :to="route + 'users/create'" class="btn btn-success">
              <i class="fa fa-plus"></i> Nuevo Usuario
            </router-link>
          </div>
        </div>

        <!-- Filters -->
        <div class="card mb-4 shadow-sm">
          <div class="card-body">
            <div class="row">
              <div class="col-md-4 col-sm-6 mb-3">
                <label for="filterSystemId">System ID (Sold-to)</label>
                <input
                  id="filterSystemId"
                  type="text"
                  class="form-control"
                  v-model="filters.systemId"
                  placeholder="Buscar por System ID"
                  @keyup.enter="search()"
                />
              </div>
              <div class="col-md-4 col-sm-6 mb-3">
                <label for="filterWebOrder">Acceso a WOE (Web Order)</label>
                <v-select
                  id="filterWebOrder"
                  :clearable="false"
                  v-model="filters.web_order"
                  :options="webOrderOptions"
                  label="label"
                  :reduce="option => option.value"
                  class="style-chooser"
                ></v-select>
              </div>
              <div class="col-md-4 col-sm-12 d-flex align-items-end mb-3">
                <button @click="search()" class="btn btn-primary mr-2" :disabled="loading">
                  <span v-if="loading" class="btn-spinner" aria-hidden="true"></span>
                  {{ loading ? 'Buscando...' : 'Buscar' }}
                </button>
                <button
                  @click="cleanFilters()"
                  class="btn btn-outline-secondary mr-2"
                  :disabled="loading"
                >
                  Limpiar Filtros
                </button>
                <button
                  @click="getUsers()"
                  class="btn btn-outline-secondary"
                  :disabled="loading"
                  title="Vuelve a consultar los datos"
                >
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-striped table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>System ID (Sold-to)</th>
                    <th>Nombre</th>
                    <th>País</th>
                    <th class="text-center">Acceso a WOE</th>
                    <th class="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="p-0">
                      <div class="loading"></div>
                    </td>
                  </tr>
                  <template v-else>
                    <tr v-for="(user, index) in users" :key="index">
                      <td>{{ user.systemId }}</td>
                      <td>{{ user.name }}</td>
                      <td>{{ user.countryName }}</td>
                      <td class="text-center">
                        <span v-if="user.web_order" class="badge badge-success">Sí</span>
                        <span v-else class="badge badge-secondary">No</span>
                      </td>
                      <td class="text-center">
                        <router-link :to="route + 'users/' + user.systemId" class="btn btn-sm btn-info text-white" title="Editar">
                          Editar
                        </router-link>
                      </td>
                    </tr>
                    <tr v-if="users.length === 0">
                      <td colspan="5" class="text-center py-4">No se encontraron usuarios.</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="row mt-4" v-if="total > itemsPerPage">
          <div class="col-md-12 text-center mb-5" :class="{ 'paginate-disabled': loading }">
            <Vue3BsPaginate
              :total="total"
              v-model="currentPage"
              :perPage="itemsPerPage"
              alignment="center"
            />
          </div>
        </div>

      </div>
    </section>
    <vue-basic-alert :duration="300" :closeIn="5000" ref="alert"/>
  </div>
</template>

<script>
import userService from '@/services/userService'
import { route } from "@/enviroment";
import { showFlashFromRoute } from "@/utils/flashAlert";
import { Vue3BsPaginate } from 'vue3-bootstrap-paginate';

export default {
  name: 'UsersList',
  components: {
    vSelect: window["vue-select"],
    Vue3BsPaginate,
  },
  data() {
    return {
      route: route,
      loading: false,
      users: [],
      currentPage: 1,
      itemsPerPage: 25,
      total: 0,
      filters: {
        systemId: '',
        web_order: null
      },
      webOrderOptions: [
        { label: 'Todos', value: null },
        { label: 'Sí', value: true },
        { label: 'No', value: false }
      ]
    }
  },
  watch: {
    currentPage() {
      this.getUsers();
    }
  },
  mounted() {
    this.getUsers();
    try {
      showFlashFromRoute(this, 'alert');
    } catch (e) {
      // ignore
    }
  },
  methods: {
    async getUsers() {
      this.loading = true;

      const params = {
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage
      };
      if (this.filters.systemId) {
        params.systemId = this.filters.systemId;
      }
      if (this.filters.web_order !== null) {
        params.web_order = this.filters.web_order;
      }

      try {
        const resp = await userService.getUsers(params);
        const data = resp.data.data;
        this.users = data.users;
        this.total = data.total;
      } catch (error) {
        console.error(error);
        this.showAlert('error', 'Error', 'Error al cargar usuarios');
      } finally {
        this.loading = false;
      }
    },
    search() {
      // a new search always restarts from the first page
      if (this.currentPage !== 1) {
        this.currentPage = 1;
        return;
      }
      this.getUsers();
    },
    cleanFilters() {
      this.filters.systemId = '';
      this.filters.web_order = null;
      this.search();
    },
    showAlert(type, title, message) {
      this.$refs.alert.showAlert(type, message, title);
    }
  }
}
</script>

<style scoped>
/* Custom overrides if needed, but relying mostly on Bootstrap 4.1 */
.user-steps {
  padding: 20px 0;
}

/* Bootstrap 4.1 predates .spinner-border, so the inline one is defined here */
.btn-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  margin-right: 0.4rem;
  vertical-align: text-bottom;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: btn-spin 0.75s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.paginate-disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
